import { Skeleton } from '@/components/ui/Skeleton';
import { cn } from '@/utils/cn';
import { HR_WEEKDAYS } from './hrCalendarUtils';

const EMPLOYEE_COL = 'min-w-[11rem] w-[11rem]';
const TOTAL_COL = 'min-w-[4.5rem] w-[4.5rem]';
const DAY_COL = 'min-w-[5.5rem] w-[5.5rem]';

export function HrEmployeeScheduleGridSkeleton({
  dayCount = 7,
  rowCount = 5,
  showTotalColumn = true,
  className,
}: {
  dayCount?: number;
  rowCount?: number;
  showTotalColumn?: boolean;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'isolate overflow-hidden rounded-lg border border-slate-200 bg-slate-50 dark:border-slate-700 dark:bg-slate-900',
        className,
      )}
      aria-busy="true"
      aria-label="Loading schedule"
    >
      <div className="flex min-w-0">
        <div
          className={cn(
            'shrink-0 border-r border-slate-200 bg-slate-50 dark:border-slate-700 dark:bg-slate-800',
            EMPLOYEE_COL,
          )}
        >
          <div className="flex h-[52px] items-end border-b border-slate-200 px-3 pb-2 dark:border-slate-700">
            <Skeleton className="h-3 w-16" />
          </div>
          {Array.from({ length: rowCount }).map((_, i) => (
            <div
              key={i}
              className="flex h-[4.5rem] items-center gap-2 border-b border-slate-100 px-2 dark:border-slate-800"
            >
              <Skeleton className="h-8 w-8 shrink-0 rounded-full" />
              <div className="min-w-0 flex-1 space-y-1.5">
                <Skeleton className="h-3.5 w-full max-w-[7rem]" />
                <Skeleton className="h-2.5 w-16" />
              </div>
            </div>
          ))}
        </div>

        <div className="min-w-0 flex-1 overflow-x-auto">
          <div className="inline-flex min-w-full flex-col">
            <div className="flex h-[52px] border-b border-slate-200 bg-slate-50 dark:border-slate-700 dark:bg-slate-800">
              {Array.from({ length: dayCount }).map((_, i) => (
                <div
                  key={i}
                  className={cn(
                    DAY_COL,
                    'flex flex-col items-center justify-end border-r border-slate-100 pb-1.5 pt-1 dark:border-slate-800',
                  )}
                >
                  <Skeleton className="h-2.5 w-7" />
                  <Skeleton className="mt-1 h-4 w-4" />
                </div>
              ))}
            </div>
            {Array.from({ length: rowCount }).map((_, row) => (
              <div key={row} className="flex">
                {Array.from({ length: dayCount }).map((_, col) => (
                  <div
                    key={col}
                    className={cn(
                      DAY_COL,
                      'flex h-[4.5rem] items-center justify-center border-b border-r border-slate-100 p-2 dark:border-slate-800',
                    )}
                  >
                    <Skeleton className="h-8 w-full max-w-[4rem] rounded-md" />
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>

        {showTotalColumn ? (
          <div
            className={cn(
              'shrink-0 border-l border-slate-200 bg-slate-50 dark:border-slate-700 dark:bg-slate-800',
              TOTAL_COL,
            )}
          >
            <div className="flex h-[52px] items-end justify-center border-b border-slate-200 pb-2 dark:border-slate-700">
              <Skeleton className="h-3 w-10" />
            </div>
            {Array.from({ length: rowCount }).map((_, i) => (
              <div
                key={i}
                className="flex h-[4.5rem] items-center justify-center border-b border-slate-100 dark:border-slate-800"
              >
                <Skeleton className="h-4 w-8" />
              </div>
            ))}
          </div>
        ) : null}
      </div>
    </div>
  );
}

export function HrMonthCalendarGridSkeleton({ className }: { className?: string }) {
  return (
    <div className={cn('space-y-2', className)} aria-busy="true" aria-label="Loading calendar">
      <div className="grid grid-cols-7 gap-1 rounded-md bg-slate-50 p-1 dark:bg-slate-800/50">
        {HR_WEEKDAYS.map((d) => (
          <div key={d} className="py-1.5 text-center text-xs font-medium text-slate-500">
            {d}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {Array.from({ length: 42 }).map((_, i) => (
          <div
            key={i}
            className="min-h-[88px] rounded-md border border-slate-200 bg-slate-50 p-2 dark:border-slate-700 dark:bg-slate-800/60"
          >
            <Skeleton className="mb-2 h-4 w-5" />
            <Skeleton className="h-6 w-full rounded-md" />
            <Skeleton className="mt-1.5 h-4 w-3/4 rounded-md" />
          </div>
        ))}
      </div>
    </div>
  );
}
